/** Format a 0-1 fraction as "67%". */
export function pct(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return "—";
  return `${Math.round(v * 100)}%`;
}

/** Format hours with one decimal, e.g. "12.5 hrs". */
export function hours(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toFixed(1)} hrs`;
}

/** Compact USD: "$14,463" or "$14.5k". `compact=true` switches to k/M
 *  abbreviation for KPI cards where space is tight. */
export function currency(
  v: number | null | undefined,
  opts: { compact?: boolean } = {},
): string {
  if (v == null || !isFinite(v)) return "—";
  if (opts.compact) {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 10_000) return `$${(v / 1000).toFixed(1)}k`;
  }
  return `$${Math.round(v).toLocaleString("en-US")}`;
}

/** Planner-wide date style (Brett, 2026-09-29): month-day-year, in
 *  prose form — "Sep 29, 2026". `shortDate` is the tight-column variant
 *  and drops the year only when it is the current year ("Sep 29"). Both
 *  tolerate bare YYYY-MM-DD and full datetime strings. */
function parseDatePart(d: string | null | undefined): Date | null {
  if (!d) return null;
  const m = String(d).match(/^(\d{4}-\d{2}-\d{2})/);
  const date = new Date((m ? m[1] : String(d)) + "T00:00:00");
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Short date: "Sep 29", or "Sep 29, 2025" when not the current year. */
export function shortDate(d: string | null | undefined): string {
  const date = parseDatePart(d);
  if (!date) return "—";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: sameYear ? undefined : "numeric" });
}

/** Full date: "Sep 29, 2026". Tolerates bare YYYY-MM-DD as well as
 *  full datetime strings — slices off the date portion before parsing. */
export function formatDate(d: string | null | undefined): string {
  const date = parseDatePart(d);
  if (!date) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Humanized date — "Nov 17, 2025". Used for hero/header surfaces where
 *  the spreadsheet "11-17-2025" reads as form output instead of prose. */
export function humanDate(d: string | null | undefined): string {
  if (!d) return "";
  const m = String(d).match(/^(\d{4}-\d{2}-\d{2})/);
  const datePart = m ? m[1] : String(d);
  const date = new Date(datePart + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Compact range. Drops redundant year/month when the two endpoints share
 *  them, so a same-year span reads "Nov 17 – May 1, 2026" and a same-month
 *  span reads "Nov 17 – 24, 2025". Returns "" if both endpoints missing. */
export function humanDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const sd = start ? new Date(String(start).slice(0, 10) + "T00:00:00") : null;
  const ed = end ? new Date(String(end).slice(0, 10) + "T00:00:00") : null;
  const sValid = sd && !Number.isNaN(sd.getTime());
  const eValid = ed && !Number.isNaN(ed.getTime());
  if (!sValid && !eValid) return "";
  if (!sValid) return humanDate(end);
  if (!eValid) return humanDate(start);
  const sameYear = sd!.getFullYear() === ed!.getFullYear();
  const sameMonth = sameYear && sd!.getMonth() === ed!.getMonth();
  const monthShort = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short" });
  if (sameMonth) {
    return `${monthShort(sd!)} ${sd!.getDate()} – ${ed!.getDate()}, ${ed!.getFullYear()}`;
  }
  if (sameYear) {
    return `${monthShort(sd!)} ${sd!.getDate()} – ${monthShort(ed!)} ${ed!.getDate()}, ${ed!.getFullYear()}`;
  }
  return `${humanDate(start)} – ${humanDate(end)}`;
}

/** Urgency bucket for a due date. Null/empty → null (nothing to show). */
export function dueUrgency(
  d: string | null | undefined,
): "overdue" | "this_week" | "later" | null {
  if (!d) return null;
  const date = new Date(d + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffDays = Math.round((date.getTime() - now.getTime()) / 86_400_000);
  if (diffDays < 0) return "overdue";
  if (diffDays <= 7) return "this_week";
  return "later";
}

/** True if the date is in the past. Null/empty → false. */
export function isOverdue(d: string | null | undefined): boolean {
  return dueUrgency(d) === "overdue";
}

/** Relative date: "3 weeks ago", "in 2 weeks". */
export function relativeDate(d: string | null | undefined): string {
  if (!d) return "—";
  const target = new Date(d + "T00:00:00");
  const now = new Date();
  const diffDays = Math.round((target.getTime() - now.getTime()) / 86_400_000);
  const weeks = Math.round(diffDays / 7);
  if (weeks === 0) return "this week";
  if (weeks > 0) return `in ${weeks}w`;
  return `${Math.abs(weeks)}w ago`;
}

/** HSL-based tone for avatar backgrounds. */
export function avatarTone(name: string): string {
  let hash = 0;
  for (const ch of name) hash = ch.charCodeAt(0) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 88%)`;
}
